// ═══════════════════════════════════════════════════════════════════════════
// TutorialPage.jsx — Aba Tutorial do Controla Caçambas
// ───────────────────────────────────────────────────────────────────────────
// Página completa de tutorial interativo com animações, diagramas SVG,
// checklist de primeiros passos, FAQ, glossário.
// Arquivo isolado do App.jsx para modularização incremental.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useMemo } from "react";

const FONT = "system-ui, -apple-system, 'Segoe UI', 'Segoe UI Variable', sans-serif";
const DISPLAY = FONT;

const C = {
  bg:        "#f6f7f9",
  surface:   "#ffffff",
  ink:       "#1e2d4f",
  sub:       "#555f6b",
  mute:      "#8a94a3",
  line:      "#e5e7eb",
  teal:      "#1a9e8f",
  tealDeep:  "#0d7a6e",
  tealSoft:  "#f0faf8",
  amber:     "#f59e0b",
  amberSoft: "#fffbeb",
  violet:    "#8b5cf6",
  violetSoft:"#f3efff",
  mint:      "#10b981",
  coral:     "#ef4444",
  sun:       "#fbbf24",
  peach:     "#fde8d7",
};

// Status do ciclo de vida (ordem do happy path)
const STATUSES = [
  { key: "Para Colocar", color: "#1a9e8f", tint: "#e6f4f1",
    resumo: "Locação criada, aguardando motorista sair pra colocar a caçamba.",
    oQueFazer: "Confirme a colocação assim que o motorista chegar no cliente." },
  { key: "Colocada",     color: "#2ecc71", tint: "#e8f8ee",
    resumo: "Caçamba já está com o cliente. Aguardando o pedido de retirada ou troca.",
    oQueFazer: "Acompanhe o tempo de permanência. Se passar do prazo, cobre." },
  { key: "Para Retirar", color: "#9b59b6", tint: "#f1e9f8",
    resumo: "Cliente solicitou retirada. Motorista precisa buscar a caçamba.",
    oQueFazer: "Programe a retirada no itinerário e confirme quando for feita." },
  { key: "Para Trocar",  color: "#f39c12", tint: "#fef3e8",
    resumo: "Cliente pediu troca: leva a cheia, deixa uma vazia nova.",
    oQueFazer: "Confirme a troca — encerra a antiga (pai) e cria uma nova (filha)." },
  { key: "Para Cobrar",  color: "#f59e0b", tint: "#fff4db",
    resumo: "Serviço finalizado, falta só receber o pagamento.",
    oQueFazer: "Confirme o pagamento e a locação vira Encerrada automaticamente." },
  { key: "Encerrada",    color: "#6b7280", tint: "#f1f3f5",
    resumo: "Locação finalizada e paga. Fica no histórico.",
    oQueFazer: "Pronto. Nada mais a fazer, só consultar quando precisar." },
  { key: "Cancelada",    color: "#e74c3c", tint: "#fde8e8",
    resumo: "Locação cancelada antes ou durante a operação.",
    oQueFazer: "Fica no histórico com motivo. Não gera cobrança." },
];

// Navegação da lateral
const NAV = [
  { group: "Comece por aqui", items: [
    { id: "boas-vindas",      label: "Bem-vindo ao Controla", icon: "👋" },
    { id: "primeiros-passos", label: "Primeiros Passos",      icon: "🚀" },
  ]},
  { group: "Como funciona", items: [
    { id: "ciclo-vida", label: "Ciclo de vida de uma locação", icon: "🔄", star: true },
    { id: "troca",      label: "Como funciona uma Troca",      icon: "♻️", star: true },
    { id: "status",     label: "Os 7 Status explicados",       icon: "🎨" },
    { id: "cadastros",  label: "Cliente, Contato e Endereço",  icon: "📇" },
  ]},
  { group: "Como fazer", items: [
    { id: "criar-locacao",   label: "Criar uma locação",          icon: "➕" },
    { id: "confirmar-coloc", label: "Confirmar colocação",        icon: "✅" },
    { id: "pedir-retirada",  label: "Solicitar retirada",          icon: "📞" },
    { id: "fazer-troca",     label: "Fazer troca",                 icon: "🔁" },
    { id: "gerar-recibo",    label: "Gerar recibo",                icon: "🧾" },
  ]},
  { group: "Referência", items: [
    { id: "faq",        label: "Perguntas frequentes", icon: "❓" },
    { id: "glossario",  label: "Glossário",            icon: "📚" },
  ]},
];

// ═══════════════════════════════════════════════════════════════════════════
// STYLES — keyframes + utilitários que precisam de CSS real
// ═══════════════════════════════════════════════════════════════════════════
function TutorialStyles() {
  return <style>{`
    @keyframes tutFadeUp {
      0% { opacity: 0; transform: translateY(14px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes tutFadeIn {
      0% { opacity: 0; }
      100% { opacity: 1; }
    }
    @keyframes tutScaleIn {
      0% { opacity: 0; transform: scale(.94); }
      100% { opacity: 1; transform: scale(1); }
    }
    @keyframes tutPulse {
      0%,100% { transform: scale(1); }
      50%     { transform: scale(1.035); }
    }
    @keyframes tutPulseBoxShadow {
      0%,100% { box-shadow: 0 0 0 0 rgba(26,158,143,.45); }
      50%     { box-shadow: 0 0 0 14px rgba(26,158,143,0); }
    }
    @keyframes tutDashFlow {
      to { stroke-dashoffset: -24; }
    }
    @keyframes tutShine {
      0%   { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes tutBounceIn {
      0%   { opacity: 0; transform: scale(.4) rotate(-8deg); }
      60%  { opacity: 1; transform: scale(1.08) rotate(3deg); }
      100% { transform: scale(1) rotate(0); }
    }
    @keyframes tutTruckDrive {
      0%   { transform: translateX(-340px); }
      35%  { transform: translateX(140px); }
      55%  { transform: translateX(140px); }
      100% { transform: translateX(520px); }
    }
    @keyframes tutCaixaOut {
      0%,30% { opacity: 1; transform: translateY(0) scale(1); }
      45%,100% { opacity: 0; transform: translateY(-60px) scale(.8); }
    }
    @keyframes tutCaixaIn {
      0%,55%  { opacity: 0; transform: translateY(-60px) scale(.8); }
      70%,100% { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes tutLinkDraw {
      0%,75%  { stroke-dashoffset: 200; opacity: 0; }
      100% { stroke-dashoffset: 0; opacity: 1; }
    }
    @keyframes tutCardRise {
      0%,70%  { opacity: 0; transform: translateY(20px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    @keyframes tutConfetti {
      0%   { opacity: 1; transform: translate(0,0) rotate(0) scale(1); }
      100% { opacity: 0; transform: translate(var(--tx), var(--ty)) rotate(var(--r)) scale(.4); }
    }
    @keyframes tutCheckDraw {
      0%   { stroke-dashoffset: 20; }
      100% { stroke-dashoffset: 0; }
    }
    @keyframes tutSlideInRight {
      0% { opacity: 0; transform: translateX(20px); }
      100% { opacity: 1; transform: translateX(0); }
    }

    .tut-root a { color: ${C.teal}; }

    .tut-nav-btn {
      display: flex; align-items: center; gap: 10px;
      width: 100%; padding: 9px 12px; min-height: 38px;
      background: transparent; border: 1px solid transparent; cursor: pointer;
      border-radius: 8px; text-align: left;
      font-family: ${FONT}; font-size: 13.5px; color: ${C.sub};
      transition: background .15s ease, color .15s ease;
      position: relative;
    }
    .tut-nav-btn:hover { background: #eef2f1; color: ${C.tealDeep}; }
    .tut-nav-btn.active {
      background: ${C.tealSoft};
      border-color: rgba(26,158,143,.3);
      color: ${C.tealDeep}; font-weight: 700;
      box-shadow: inset 3px 0 0 ${C.teal};
    }
    .tut-nav-btn.active .tut-nav-dot { background: ${C.teal}; }

    .tut-nav-dot {
      width: 4px; height: 4px; border-radius: 50%;
      background: ${C.mute}; margin-left: auto;
    }
    .tut-nav-btn.star::after {
      content: '★'; color: ${C.amber}; font-size: 11px;
      margin-left: auto;
    }
    .tut-nav-btn.active.star::after { color: ${C.sun}; }

    .tut-card {
      background: ${C.surface};
      border: 1px solid ${C.line};
      border-radius: 12px;
      padding: 22px;
      box-shadow: 0 1px 3px rgba(0,0,0,.04);
      transition: box-shadow .2s ease, border-color .2s ease;
    }
    .tut-card:hover {
      border-color: #c6e6e0;
      box-shadow: 0 4px 14px rgba(14,26,31,.07);
    }

    .tut-pill {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 4px 10px; border-radius: 999px;
      font-size: 11px; font-weight: 700; letter-spacing: .3px;
      text-transform: uppercase;
    }

    .tut-kbd {
      display: inline-block; padding: 2px 8px;
      background: ${C.surface}; border: 1px solid ${C.line};
      border-bottom-width: 2px; border-radius: 4px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      font-size: 11px; color: ${C.ink};
    }

    .tut-status-node {
      cursor: pointer;
      transition: filter .2s ease;
    }
    .tut-status-node:hover { filter: brightness(1.08) drop-shadow(0 4px 10px rgba(14,26,31,.08)); }
    .tut-status-node.active .tut-node-body {
      animation: tutPulse 2s ease-out infinite;
      transform-box: fill-box;
      transform-origin: center;
    }
    .tut-node-body {
      transition: transform .2s ease;
      transform-box: fill-box;
      transform-origin: center;
    }
    .tut-status-node:hover .tut-node-body {
      transform: scale(1.05);
    }

    .tut-arrow path.flow {
      stroke-dasharray: 6 6;
      animation: tutDashFlow 1.2s linear infinite;
    }

    .tut-accordion-item {
      border: 1px solid ${C.line};
      border-radius: 10px;
      margin-bottom: 8px;
      background: ${C.surface};
      overflow: hidden;
      transition: box-shadow .2s ease;
    }
    .tut-accordion-item.open { box-shadow: 0 4px 16px rgba(14,26,31,.05); }
    .tut-accordion-head {
      padding: 14px 18px;
      display: flex; align-items: center; gap: 12px;
      cursor: pointer; user-select: none;
      background: transparent; border: none; width: 100%;
      font-family: ${FONT}; font-size: 14px; color: ${C.ink};
      text-align: left; font-weight: 600;
    }
    .tut-accordion-head:hover { background: ${C.tealSoft}; }
    .tut-accordion-icon {
      margin-left: auto; transition: transform .3s cubic-bezier(.4,0,.2,1);
      color: ${C.teal}; font-size: 18px; font-weight: 300;
    }
    .tut-accordion-item.open .tut-accordion-icon { transform: rotate(45deg); }
    .tut-accordion-body {
      max-height: 0; overflow: hidden;
      transition: max-height .35s cubic-bezier(.4,0,.2,1);
    }
    .tut-accordion-item.open .tut-accordion-body { max-height: 600px; }
    .tut-accordion-inner { padding: 0 18px 16px; font-size: 14px; color: ${C.sub}; line-height: 1.65; }

    .tut-step {
      display: flex; gap: 16px; padding: 14px 0;
      border-top: 1px dashed ${C.line};
    }
    .tut-step:first-child { border-top: none; }
    .tut-step-num {
      flex-shrink: 0; width: 30px; height: 30px; border-radius: 50%;
      background: ${C.teal}; color: #fff;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 13.5px;
      font-family: ${FONT};
      box-shadow: 0 1px 3px rgba(26,158,143,.35);
    }

    .tut-check-item {
      display: flex; align-items: center; gap: 14px;
      padding: 14px 18px;
      background: ${C.surface};
      border: 1px solid ${C.line};
      border-radius: 12px;
      margin-bottom: 8px;
      cursor: pointer;
      transition: all .2s ease;
      position: relative;
      overflow: hidden;
    }
    .tut-check-item:hover {
      border-color: ${C.teal};
      transform: translateX(4px);
    }
    .tut-check-item.done {
      background: linear-gradient(90deg, ${C.tealSoft} 0%, ${C.surface} 80%);
      border-color: ${C.teal};
    }
    .tut-check-item.done .tut-check-label {
      text-decoration: line-through;
      color: ${C.mute};
    }
    .tut-check-box {
      flex-shrink: 0; width: 28px; height: 28px; border-radius: 50%;
      border: 2px solid ${C.line}; background: ${C.surface};
      display: flex; align-items: center; justify-content: center;
      transition: all .3s cubic-bezier(.4,0,.2,1);
    }
    .tut-check-item.done .tut-check-box {
      background: ${C.teal}; border-color: ${C.teal};
      transform: scale(1.08);
    }
    .tut-check-svg {
      stroke-dasharray: 20; stroke-dashoffset: 20;
      transition: stroke-dashoffset .4s ease .1s;
    }
    .tut-check-item.done .tut-check-svg {
      stroke-dashoffset: 0;
    }

    .tut-progress-track {
      height: 10px; background: ${C.line}; border-radius: 999px;
      overflow: hidden; position: relative;
    }
    .tut-progress-fill {
      height: 100%;
      background: linear-gradient(90deg, ${C.teal} 0%, ${C.mint} 100%);
      border-radius: 999px;
      transition: width .6s cubic-bezier(.34,1.56,.64,1);
      position: relative;
    }
    .tut-progress-fill::after {
      content: ''; position: absolute; inset: 0;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,.4), transparent);
      background-size: 200% 100%;
      animation: tutShine 2.4s linear infinite;
    }

    .tut-gif-ph {
      position: relative; aspect-ratio: 16 / 9;
      background: #f8fafc;
      border: 1px dashed #9fd4cc;
      border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      overflow: hidden;
    }

    .tut-btn-primary {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      padding: 11px 20px; min-height: 44px;
      background: ${C.teal};
      color: #fff; border: none; border-radius: 10px;
      font-family: ${FONT}; font-size: 14px; font-weight: 700;
      cursor: pointer; transition: background .2s ease, box-shadow .2s ease;
      box-shadow: 0 1px 3px rgba(26,158,143,.35);
    }
    .tut-btn-primary:hover {
      background: ${C.tealDeep};
      box-shadow: 0 4px 12px rgba(26,158,143,.3);
    }
    .tut-btn-ghost {
      display: inline-flex; align-items: center; justify-content: center; gap: 6px;
      padding: 10px 16px; min-height: 44px; background: #fff;
      border: 1px solid ${C.line}; border-radius: 10px;
      font-family: ${FONT}; font-size: 13.5px; font-weight: 600;
      color: ${C.sub}; cursor: pointer;
      transition: all .2s ease;
    }
    .tut-btn-ghost:hover { border-color: ${C.teal}; color: ${C.tealDeep}; background: ${C.tealSoft}; }

    .tut-search-wrap { position: relative; max-width: 560px; }
    .tut-search-ico {
      position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
      color: ${C.mute}; pointer-events: none; display: flex;
    }
    .tut-search-input {
      width: 100%; box-sizing: border-box;
      padding: 12px 16px 12px 42px; min-height: 46px;
      border: 1px solid ${C.line}; border-radius: 10px;
      font-family: ${FONT}; font-size: 14px; color: ${C.ink};
      background: #fff; outline: none;
      box-shadow: 0 1px 3px rgba(0,0,0,.04);
      transition: border-color .15s ease, box-shadow .15s ease;
    }
    .tut-search-input::placeholder { color: ${C.mute}; }
    .tut-search-input:focus {
      border-color: ${C.teal};
      box-shadow: 0 0 0 3px rgba(26,158,143,.14);
    }
    .tut-search-results {
      position: absolute; z-index: 20; top: calc(100% + 6px); left: 0; right: 0;
      background: #fff; border: 1px solid ${C.line}; border-radius: 12px;
      box-shadow: 0 10px 30px rgba(14,26,31,.12);
      overflow: hidden;
      animation: tutFadeIn .15s ease both;
    }
    .tut-search-item {
      display: flex; align-items: center; gap: 10px;
      width: 100%; padding: 11px 14px; min-height: 44px;
      background: #fff; border: none; border-bottom: 1px solid #f1f3f5;
      cursor: pointer; text-align: left;
      font-family: ${FONT}; font-size: 13.5px; color: ${C.ink};
    }
    .tut-search-item:last-child { border-bottom: none; }
    .tut-search-item:hover { background: ${C.tealSoft}; }
    .tut-search-tag {
      margin-left: auto; flex-shrink: 0;
      font-size: 10.5px; font-weight: 700; letter-spacing: .4px;
      color: ${C.tealDeep}; background: ${C.tealSoft};
      border: 1px solid rgba(26,158,143,.25);
      padding: 2px 8px; border-radius: 999px;
      text-transform: uppercase;
    }

    .tut-anim-in { animation: tutFadeUp .5s cubic-bezier(.4,0,.2,1) both; }
    .tut-anim-stagger > * { animation: tutFadeUp .5s cubic-bezier(.4,0,.2,1) both; }
    .tut-anim-stagger > *:nth-child(1) { animation-delay: .05s; }
    .tut-anim-stagger > *:nth-child(2) { animation-delay: .12s; }
    .tut-anim-stagger > *:nth-child(3) { animation-delay: .19s; }
    .tut-anim-stagger > *:nth-child(4) { animation-delay: .26s; }
    .tut-anim-stagger > *:nth-child(5) { animation-delay: .33s; }
    .tut-anim-stagger > *:nth-child(6) { animation-delay: .40s; }
    .tut-anim-stagger > *:nth-child(7) { animation-delay: .47s; }
    .tut-anim-stagger > *:nth-child(8) { animation-delay: .54s; }

    .tut-confetti-piece {
      position: absolute; width: 10px; height: 10px;
      top: 50%; left: 50%;
      animation: tutConfetti 1s cubic-bezier(.2,.8,.3,1) forwards;
    }

    @media (max-width: 900px) {
      .tut-layout { grid-template-columns: 1fr !important; gap: 16px !important; }
      .tut-sidebar {
        position: static !important; max-height: none !important;
        display: flex; align-items: center; gap: 8px;
        overflow-x: auto; -webkit-overflow-scrolling: touch;
        padding: 4px 2px 10px !important;
      }
      .tut-nav-group { display: contents; }
      .tut-nav-group-title { display: none !important; }
      .tut-nav-btn {
        width: auto; flex: 0 0 auto; white-space: nowrap;
        min-height: 44px; padding: 10px 14px;
        background: #fff; border: 1px solid ${C.line}; border-radius: 999px;
      }
      .tut-nav-btn.active {
        box-shadow: none;
        background: ${C.tealSoft}; border-color: ${C.teal};
      }
      .tut-nav-btn.star::after { display: none; }
      .tut-nav-dot { display: none; }
    }
    @media (max-width: 768px) {
      .tut-root { padding: 12px 12px 32px !important; }
      .tut-hero { padding: 20px 16px 24px !important; }
      .tut-hero-actions { flex-direction: column; align-items: stretch; }
      .tut-grid-2 { grid-template-columns: 1fr !important; }
      .tut-troca-cards { grid-template-columns: 1fr !important; justify-items: stretch; }
      .tut-troca-link { justify-self: center; }
      .tut-article-title { font-size: 19px !important; }
      .tut-accordion-head { min-height: 48px; }
      .tut-card { padding: 16px; }
    }
  `}</style>;
}

// ═══════════════════════════════════════════════════════════════════════════
// SVG ICONS — caçamba, caminhão, cliente, prédio, etc (feitas à mão)
// ═══════════════════════════════════════════════════════════════════════════
const CacambaSvg = ({ size = 48, color = C.teal, tag = "", stroke = "#0a0a0a" }) => (
  <svg width={size} height={size * 0.75} viewBox="0 0 100 75" style={{ display: "block" }}>
    <defs>
      <linearGradient id={`cac-grad-${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={color} />
        <stop offset="1" stopColor={color} stopOpacity=".75" />
      </linearGradient>
    </defs>
    {/* Corpo trapezoidal */}
    <path d="M 8 18 L 92 18 L 85 68 L 15 68 Z" fill={`url(#cac-grad-${color.replace("#","")})`} stroke={stroke} strokeWidth="2" strokeLinejoin="round"/>
    {/* Borda superior */}
    <path d="M 6 16 L 94 16 L 92 22 L 8 22 Z" fill={color} stroke={stroke} strokeWidth="2" strokeLinejoin="round" opacity="0.85"/>
    {/* Linhas verticais de reforço */}
    <line x1="30" y1="22" x2="27" y2="66" stroke={stroke} strokeWidth="1.2" opacity=".35"/>
    <line x1="50" y1="22" x2="50" y2="66" stroke={stroke} strokeWidth="1.2" opacity=".35"/>
    <line x1="70" y1="22" x2="73" y2="66" stroke={stroke} strokeWidth="1.2" opacity=".35"/>
    {/* Pézinhos */}
    <rect x="15" y="68" width="6" height="3" fill={stroke} opacity=".6"/>
    <rect x="79" y="68" width="6" height="3" fill={stroke} opacity=".6"/>
    {tag && (
      <g>
        <rect x="38" y="36" width="24" height="14" rx="3" fill="#fff" stroke={stroke} strokeWidth="1.5"/>
        <text x="50" y="46" textAnchor="middle" fontSize="9" fontWeight="800" fill={stroke} fontFamily="ui-monospace, monospace">{tag}</text>
      </g>
    )}
  </svg>
);

const CaminhaoSvg = ({ size = 90 }) => (
  <svg width={size} height={size * 0.55} viewBox="0 0 180 100" style={{ display: "block" }}>
    {/* Cabine */}
    <path d="M 12 30 L 40 30 L 46 52 L 12 52 Z" fill="#1a9e8f" stroke="#0a0a0a" strokeWidth="2" strokeLinejoin="round"/>
    {/* Vidro */}
    <path d="M 18 34 L 36 34 L 40 48 L 18 48 Z" fill="#b8e3dc" stroke="#0a0a0a" strokeWidth="1.5"/>
    {/* Caçamba em cima */}
    <path d="M 46 22 L 150 22 L 144 62 L 50 62 Z" fill="#f59e0b" stroke="#0a0a0a" strokeWidth="2" strokeLinejoin="round"/>
    <line x1="70" y1="24" x2="68" y2="60" stroke="#0a0a0a" strokeWidth="1" opacity=".4"/>
    <line x1="95" y1="24" x2="95" y2="60" stroke="#0a0a0a" strokeWidth="1" opacity=".4"/>
    <line x1="120" y1="24" x2="122" y2="60" stroke="#0a0a0a" strokeWidth="1" opacity=".4"/>
    {/* Rodas */}
    <circle cx="30" cy="70" r="10" fill="#2b2b2b" stroke="#0a0a0a" strokeWidth="2"/>
    <circle cx="30" cy="70" r="4" fill="#666"/>
    <circle cx="70" cy="70" r="10" fill="#2b2b2b" stroke="#0a0a0a" strokeWidth="2"/>
    <circle cx="70" cy="70" r="4" fill="#666"/>
    <circle cx="130" cy="70" r="10" fill="#2b2b2b" stroke="#0a0a0a" strokeWidth="2"/>
    <circle cx="130" cy="70" r="4" fill="#666"/>
    {/* Base */}
    <line x1="8" y1="82" x2="160" y2="82" stroke="#0a0a0a" strokeWidth="2"/>
  </svg>
);

const ClienteSvg = ({ size = 64 }) => (
  <svg width={size} height={size * 1.15} viewBox="0 0 64 74" style={{ display: "block" }}>
    <path d="M 8 70 L 8 26 L 32 10 L 56 26 L 56 70 Z" fill="#fde8d7" stroke="#0a0a0a" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M 6 28 L 32 10 L 58 28" fill="none" stroke="#0a0a0a" strokeWidth="2" strokeLinejoin="round"/>
    <rect x="16" y="34" width="10" height="12" fill="#b8e3dc" stroke="#0a0a0a" strokeWidth="1.5"/>
    <rect x="38" y="34" width="10" height="12" fill="#b8e3dc" stroke="#0a0a0a" strokeWidth="1.5"/>
    <rect x="26" y="54" width="12" height="16" fill="#0d7a6e" stroke="#0a0a0a" strokeWidth="1.5"/>
    <circle cx="35" cy="62" r="1" fill="#fbbf24"/>
    <line x1="4" y1="72" x2="60" y2="72" stroke="#0a0a0a" strokeWidth="2"/>
  </svg>
);

// ═══════════════════════════════════════════════════════════════════════════
// HERO — primeira impressão, animado, com decorações flutuantes
// ═══════════════════════════════════════════════════════════════════════════
function Hero({ onStart }) {
  const [busca, setBusca] = useState("");

  const resultados = useMemo(() => {
    const s = busca.trim().toLowerCase();
    if (s.length < 2) return [];
    const out = [];
    NAV.forEach(g => g.items.forEach(it => {
      if (it.label.toLowerCase().includes(s)) {
        out.push({ id: it.id, label: it.label, icon: it.icon, tag: "Tópico" });
      }
    }));
    FAQ_ITEMS.forEach(f => {
      if (f.q.toLowerCase().includes(s)) {
        out.push({ id: "faq", label: f.q, icon: "❓", tag: "FAQ" });
      }
    });
    GLOSSARIO.forEach(g => {
      if ((g.termo + " " + g.def).toLowerCase().includes(s)) {
        out.push({ id: "glossario", label: g.termo, icon: "📚", tag: "Glossário" });
      }
    });
    return out.slice(0, 8);
  }, [busca]);

  const irPara = (id) => { setBusca(""); onStart(id); };

  return (
    <div className="tut-hero" style={{
      position: "relative",
      background: C.surface,
      borderRadius: 14,
      padding: "32px 32px 34px",
      border: `1px solid ${C.line}`,
      boxShadow: "0 1px 3px rgba(0,0,0,.04)",
    }}>
      <div style={{ position: "relative", maxWidth: 680 }}>
        <div className="tut-anim-in" style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "4px 12px",
          background: `${C.tealSoft}`,
          border: `1px solid rgba(26,158,143,.25)`,
          borderRadius: 999, marginBottom: 14,
          fontSize: 11, fontWeight: 700, color: C.tealDeep,
          letterSpacing: .6, textTransform: "uppercase",
        }}>
          Central de aprendizado do Controla
        </div>

        <h1 className="tut-anim-in" style={{
          fontFamily: FONT,
          fontWeight: 800,
          fontSize: 24,
          lineHeight: 1.25,
          letterSpacing: "-.3px",
          color: C.ink,
          margin: "0 0 8px",
          animationDelay: ".06s",
        }}>
          Entenda o Controla de um jeito simples
        </h1>

        <p className="tut-anim-in" style={{
          fontSize: 14,
          lineHeight: 1.6,
          color: C.sub,
          margin: "0 0 20px",
          maxWidth: 560,
          animationDelay: ".12s",
        }}>
          Tudo que você precisa saber pra usar o sistema sem se perder — <strong style={{ color: C.ink }}>do zero até dominar</strong>.
          Diagramas animados, passo-a-passo e explicações curtas.
          Sem enrolação.
        </p>

        <div className="tut-search-wrap tut-anim-in" style={{ marginBottom: 18, animationDelay: ".18s" }}>
          <span className="tut-search-ico">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7"/><path d="M 21 21 L 16.5 16.5"/>
            </svg>
          </span>
          <input
            className="tut-search-input"
            type="text"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Buscar no tutorial: troca, recibo, status, retirada…"
          />
          {resultados.length > 0 && (
            <div className="tut-search-results">
              {resultados.map((r, i) => (
                <button key={`${r.id}-${i}`} className="tut-search-item" onClick={() => irPara(r.id)}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{r.icon}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</span>
                  <span className="tut-search-tag">{r.tag}</span>
                </button>
              ))}
            </div>
          )}
          {busca.trim().length >= 2 && resultados.length === 0 && (
            <div className="tut-search-results">
              <div style={{ padding: "12px 14px", fontSize: 13, color: C.mute, fontFamily: FONT }}>
                Nada encontrado. Tente outra palavra ou veja as Perguntas frequentes.
              </div>
            </div>
          )}
        </div>

        <div className="tut-hero-actions tut-anim-in" style={{ display: "flex", gap: 10, animationDelay: ".24s" }}>
          <button className="tut-btn-primary" onClick={() => onStart("primeiros-passos")}>
            Começar do zero
          </button>
          <button className="tut-btn-ghost" onClick={() => onStart("ciclo-vida")}>
            Entender o ciclo de uma locação →
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SIDEBAR — navegação
// ═══════════════════════════════════════════════════════════════════════════
function Sidebar({ active, onSelect }) {
  return (
    <aside className="tut-sidebar" style={{
      position: "sticky",
      top: 24,
      alignSelf: "start",
      maxHeight: "calc(100vh - 48px)",
      overflowY: "auto",
      padding: "4px 4px 4px 0",
    }}>
      {NAV.map(group => (
        <div key={group.group} className="tut-nav-group" style={{ marginBottom: 22 }}>
          <div className="tut-nav-group-title" style={{
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: C.mute,
            padding: "0 12px 8px",
          }}>
            {group.group}
          </div>
          {group.items.map(it => (
            <button
              key={it.id}
              className={`tut-nav-btn ${active === it.id ? "active" : ""} ${it.star ? "star" : ""}`}
              onClick={() => onSelect(it.id)}
            >
              <span style={{ fontSize: 14 }}>{it.icon}</span>
              <span style={{ flex: 1 }}>{it.label}</span>
            </button>
          ))}
        </div>
      ))}
    </aside>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ARTICLE HEADER — título padronizado de artigo
// ═══════════════════════════════════════════════════════════════════════════
function ArticleHeader({ eyebrow, title, subtitle, emoji }) {
  return (
    <header style={{ marginBottom: 28 }} className="tut-anim-in">
      {eyebrow && (
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: 1.2,
          color: C.teal, textTransform: "uppercase", marginBottom: 8,
        }}>
          {eyebrow}
        </div>
      )}
      <h2 className="tut-article-title" style={{
        fontFamily: FONT,
        fontSize: 22, fontWeight: 800, lineHeight: 1.25,
        letterSpacing: "-.3px",
        color: C.ink, margin: "0 0 10px",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        {emoji && (
          <span style={{
            flexShrink: 0,
            width: 36, height: 36, borderRadius: 10,
            background: C.tealSoft, border: "1px solid rgba(26,158,143,.22)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: 17,
          }}>{emoji}</span>
        )}
        {title}
      </h2>
      {subtitle && (
        <p style={{
          fontSize: 14, lineHeight: 1.6, color: C.sub,
          margin: 0, maxWidth: 720,
        }}>
          {subtitle}
        </p>
      )}
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CICLO DE VIDA — DIAGRAMA ANIMADO (a estrela)
// ═══════════════════════════════════════════════════════════════════════════
function CicloVidaDiagram() {
  const [selected, setSelected] = useState("Para Colocar");

  // Posições dos nodes no SVG (happy path no topo, desvios embaixo)
  const layout = {
    "Para Colocar": { x: 80,  y: 70,  w: 150 },
    "Colocada":     { x: 290, y: 70,  w: 130 },
    "Para Retirar": { x: 490, y: 70,  w: 150 },
    "Para Cobrar":  { x: 700, y: 70,  w: 150 },
    "Encerrada":    { x: 910, y: 70,  w: 130 },
    "Para Trocar":  { x: 490, y: 195, w: 150 },
    "Cancelada":    { x: 80,  y: 195, w: 130 },
  };

  const st = STATUSES.find(s => s.key === selected);

  return (
    <div>
      <div style={{
        background: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        padding: "28px 24px 20px",
        overflowX: "auto",
      }}>
        <svg width="1080" height="280" viewBox="0 0 1080 280" style={{ maxWidth: "100%", height: "auto" }}>
          {/* Setas do happy path */}
          <g className="tut-arrow">
            {[
              { from: "Para Colocar", to: "Colocada" },
              { from: "Colocada", to: "Para Retirar" },
              { from: "Para Retirar", to: "Para Cobrar" },
              { from: "Para Cobrar", to: "Encerrada" },
            ].map((a, i) => {
              const f = layout[a.from], t = layout[a.to];
              const x1 = f.x + f.w, x2 = t.x, y = f.y + 26;
              return (
                <g key={i}>
                  <path d={`M ${x1} ${y} L ${x2 - 8} ${y}`}
                        stroke={C.teal} strokeWidth="2.5" fill="none"
                        className="flow" />
                  <path d={`M ${x2 - 10} ${y - 5} L ${x2} ${y} L ${x2 - 10} ${y + 5}`}
                        stroke={C.teal} strokeWidth="2.5" fill={C.teal} strokeLinejoin="round"/>
                </g>
              );
            })}

            {/* Setas dos desvios: Colocada → Para Trocar, Para Trocar → Encerrada */}
            {/* Colocada → Para Trocar (curva pra baixo) */}
            <path d="M 355 122 C 355 160, 565 160, 565 190" stroke={C.amber}
                  strokeWidth="2" fill="none" strokeDasharray="5 5" />
            <path d="M 560 186 L 565 194 L 570 186" stroke={C.amber} strokeWidth="2" fill={C.amber}/>
            <text x="425" y="158" fontSize="11" fill={C.amber} fontWeight="700" fontFamily={FONT}>
              pedido de troca
            </text>

            {/* Para Trocar → Encerrada (curva de volta ao topo) */}
            <path d="M 640 210 C 800 230, 900 170, 975 122" stroke={C.amber}
                  strokeWidth="2" fill="none" strokeDasharray="5 5" />
            <path d="M 969 119 L 977 122 L 973 129" stroke={C.amber} strokeWidth="2" fill={C.amber}/>
            <text x="780" y="215" fontSize="11" fill={C.amber} fontWeight="700" fontFamily={FONT}>
              troca confirmada
            </text>

            {/* Para Colocar → Cancelada */}
            <path d="M 155 122 C 155 160, 145 160, 145 190" stroke={C.coral}
                  strokeWidth="2" fill="none" strokeDasharray="5 5" />
            <path d="M 140 186 L 145 194 L 150 186" stroke={C.coral} strokeWidth="2" fill={C.coral}/>
            <text x="-4" y="158" fontSize="11" fill={C.coral} fontWeight="700" fontFamily={FONT} transform="translate(170 0)">
              cancelada
            </text>
          </g>

          {/* Nodes de status */}
          {STATUSES.map(s => {
            const p = layout[s.key];
            if (!p) return null;
            const isSelected = s.key === selected;
            return (
              <g key={s.key} className={`tut-status-node ${isSelected ? "active" : ""}`}
                 onClick={() => setSelected(s.key)}
                 transform={`translate(${p.x}, ${p.y})`}>
                <g className="tut-node-body">
                  <rect x="0" y="0" width={p.w} height="52" rx="12"
                        fill={isSelected ? s.color : s.tint}
                        stroke={s.color} strokeWidth={isSelected ? 2.5 : 1.5}/>
                  <text x={p.w / 2} y="22" textAnchor="middle"
                        fontSize="13" fontWeight="800"
                        fill={isSelected ? "#fff" : s.color}
                        fontFamily={FONT}>
                    {s.key}
                  </text>
                  <circle cx={p.w / 2} cy="38" r="3"
                          fill={isSelected ? "#fff" : s.color}/>
                </g>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Card detalhado do status selecionado */}
      <div key={selected} className="tut-anim-in" style={{
        marginTop: 20,
        background: C.surface,
        border: `1px solid ${C.line}`,
        borderLeft: `4px solid ${st.color}`,
        borderRadius: 12,
        padding: "20px 24px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <span className="tut-pill" style={{ background: st.tint, color: st.color }}>
            {st.key}
          </span>
          <span style={{ fontSize: 12, color: C.mute }}>clique em outro status pra explorar</span>
        </div>
        <p style={{ margin: "0 0 10px", fontSize: 15, color: C.ink, lineHeight: 1.55 }}>
          <strong>O que significa:</strong> {st.resumo}
        </p>
        <p style={{ margin: 0, fontSize: 14, color: C.sub, lineHeight: 1.55 }}>
          <strong style={{ color: C.tealDeep }}>→ O que você faz:</strong> {st.oQueFazer}
        </p>
      </div>

      {/* Legenda */}
      <div style={{ display: "flex", gap: 16, marginTop: 14, flexWrap: "wrap", fontSize: 12, color: C.sub }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 18, height: 3, background: C.teal, borderRadius: 2 }}/>
          Caminho normal
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 18, height: 3, background: C.amber, borderRadius: 2,
            backgroundImage: `repeating-linear-gradient(90deg, ${C.amber} 0 3px, transparent 3px 6px)` }}/>
          Desvio: troca
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 18, height: 3, borderRadius: 2,
            backgroundImage: `repeating-linear-gradient(90deg, ${C.coral} 0 3px, transparent 3px 6px)` }}/>
          Cancelamento
        </span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TROCA ANIMAÇÃO — cena animada com caminhão e caçambas
// ═══════════════════════════════════════════════════════════════════════════
function TrocaAnimacao() {
  const [playing, setPlaying] = useState(0);
  const replay = () => setPlaying(p => p + 1);

  useEffect(() => {
    const t = setTimeout(() => replay(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <div style={{
        position: "relative",
        background: `
          linear-gradient(180deg, #e8f5f0 0%, #fafaf7 70%, #fef3e8 100%)
        `,
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        boxShadow: "0 1px 3px rgba(0,0,0,.04)",
        padding: "30px 0 40px",
        overflow: "hidden",
        minHeight: 260,
      }}>
        {/* Céu/chão decorativo */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 60,
          background: `linear-gradient(180deg, transparent 0%, #e8d4b0 70%)`,
          opacity: .6,
        }}/>
        <div style={{
          position: "absolute", bottom: 70, left: 0, right: 0, height: 1,
          background: C.line,
        }}/>

        <div key={playing} style={{
          position: "relative",
          width: "100%",
          height: 180,
        }}>
          {/* Cliente à direita */}
          <div style={{
            position: "absolute", right: 40, bottom: 50,
            zIndex: 3,
          }}>
            <ClienteSvg size={70} />
            <div style={{
              position: "absolute", top: -22, left: "50%", transform: "translateX(-50%)",
              fontSize: 11, fontWeight: 700, color: C.sub,
              background: "#fff", padding: "2px 8px", borderRadius: 10,
              border: `1px solid ${C.line}`, whiteSpace: "nowrap",
            }}>
              Cliente
            </div>
          </div>

          {/* Caçamba 1 (vai embora) */}
          <div style={{
            position: "absolute",
            right: 130, bottom: 50,
            animation: "tutCaixaOut 4.5s cubic-bezier(.4,0,.2,1) forwards",
            animationDelay: ".4s",
            zIndex: 2,
          }}>
            <CacambaSvg size={70} color="#2ecc71" tag="#100" />
            <div style={{
              position: "absolute", top: -18, left: "50%", transform: "translateX(-50%)",
              fontSize: 10, fontWeight: 800, color: "#2ecc71",
              whiteSpace: "nowrap",
            }}>
              (cheia)
            </div>
          </div>

          {/* Caminhão entra com caçamba 2 e sai sem ela */}
          <div style={{
            position: "absolute",
            left: -200, bottom: 48,
            animation: "tutTruckDrive 4.5s cubic-bezier(.55,.2,.45,.8) forwards",
            animationDelay: ".4s",
            zIndex: 1,
          }}>
            <CaminhaoSvg size={110} />
          </div>

          {/* Caçamba 2 (fica no cliente) */}
          <div style={{
            position: "absolute",
            right: 130, bottom: 50,
            animation: "tutCaixaIn 4.5s cubic-bezier(.4,0,.2,1) forwards",
            animationDelay: ".4s",
            zIndex: 2,
          }}>
            <CacambaSvg size={70} color={C.teal} tag="#101" />
            <div style={{
              position: "absolute", top: -18, left: "50%", transform: "translateX(-50%)",
              fontSize: 10, fontWeight: 800, color: C.tealDeep,
              whiteSpace: "nowrap",
            }}>
              (vazia)
            </div>
          </div>
        </div>

        {/* Botão de replay */}
        <div style={{
          position: "absolute", top: 14, right: 14,
        }}>
          <button onClick={replay} className="tut-btn-ghost" style={{ padding: "6px 12px", fontSize: 12 }}>
            ↻ Ver de novo
          </button>
        </div>
      </div>

      {/* Cartões vinculados que aparecem depois da animação */}
      <div key={`cards-${playing}`} className="tut-troca-cards" style={{
        display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 10, alignItems: "center",
        marginTop: 24,
      }}>
        <div style={{
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderLeft: `4px solid #6b7280`,
          borderRadius: 12,
          padding: "16px 18px",
          animation: "tutCardRise 1.2s cubic-bezier(.4,0,.2,1) both",
          animationDelay: "3.2s",
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.mute, letterSpacing: 1 }}>
            LOCAÇÃO ANTIGA (PAI)
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.ink, fontFamily: DISPLAY, margin: "4px 0" }}>
            #100
          </div>
          <span className="tut-pill" style={{ background: "#f1f3f5", color: "#6b7280" }}>Encerrada</span>
          <div style={{ fontSize: 12, color: C.sub, marginTop: 8 }}>
            Caçamba cheia recolhida
          </div>
        </div>

        <div className="tut-troca-link" style={{
          width: 60, height: 2,
          backgroundImage: `repeating-linear-gradient(90deg, ${C.amber} 0 4px, transparent 4px 8px)`,
          position: "relative",
          animation: "tutFadeIn .5s both",
          animationDelay: "3.6s",
        }}>
          <div style={{
            position: "absolute",
            top: -16, left: "50%", transform: "translateX(-50%)",
            background: C.amberSoft, color: C.amber,
            fontSize: 10, fontWeight: 800, letterSpacing: .6,
            padding: "2px 8px", borderRadius: 10, border: `1px solid ${C.amber}55`,
            whiteSpace: "nowrap",
          }}>
            VÍNCULO
          </div>
        </div>

        <div style={{
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderLeft: `4px solid ${C.teal}`,
          borderRadius: 12,
          padding: "16px 18px",
          animation: "tutCardRise 1.2s cubic-bezier(.4,0,.2,1) both",
          animationDelay: "3.4s",
        }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.mute, letterSpacing: 1 }}>
            NOVA LOCAÇÃO (FILHA)
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.ink, fontFamily: DISPLAY, margin: "4px 0" }}>
            #101
          </div>
          <span className="tut-pill" style={{ background: C.tealSoft, color: C.tealDeep }}>Colocada</span>
          <div style={{ fontSize: 12, color: C.sub, marginTop: 8 }}>
            Caçamba vazia no mesmo cliente
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CHECKLIST DE PRIMEIROS PASSOS
// ═══════════════════════════════════════════════════════════════════════════
const PASSOS = [
  { id: 1, titulo: "Cadastre os dados da sua empresa",
    descricao: "Nome, CNPJ, endereço, logo e chave PIX. Aparece em todos os recibos.",
    onde: "Configurações → Empresa",
  },
  { id: 2, titulo: "Crie os tipos de caçamba que você opera",
    descricao: "Ex: 3 m³, 5 m³, 7 m³. Cada tipo com cubicagem e valor padrão.",
    onde: "Tipos de Caçamba → Nova",
  },
  { id: 3, titulo: "Adicione suas caçambas",
    descricao: "Uma por uma ou em lote. Cada caçamba recebe um código único.",
    onde: "Caçambas → Nova Caçamba",
  },
  { id: 4, titulo: "Cadastre os motoristas",
    descricao: "Nome, telefone, CNH. Você pode criar senha de acesso ao app do motorista.",
    onde: "Motoristas → Novo",
  },
  { id: 5, titulo: "Registre os veículos da frota",
    descricao: "Caminhões com placa, modelo e quilometragem atual.",
    onde: "Veículos → Novo Veículo",
  },
  { id: 6, titulo: "Crie seu primeiro cliente",
    descricao: "Nome ou razão social, CPF/CNPJ, telefone e endereço principal.",
    onde: "Clientes → Novo Cliente",
  },
  { id: 7, titulo: "Registre sua primeira locação",
    descricao: "Escolha o cliente, o tipo de caçamba e o endereço. Atribua motorista.",
    onde: "Locações → Nova Locação",
  },
  { id: 8, titulo: "Confirme a colocação e gere o recibo",
    descricao: "Assim que o motorista chegar no cliente, confirme. Depois é só imprimir o recibo.",
    onde: "Locações → menu da locação",
  },
];

function PrimeirosPassosChecklist() {
  const [done, setDone] = useState(new Set());
  const [confetti, setConfetti] = useState(0);

  const toggle = (id) => {
    setDone(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); }
      else {
        next.add(id);
        if (next.size === PASSOS.length) setConfetti(c => c + 1);
      }
      return next;
    });
  };

  const pct = Math.round((done.size / PASSOS.length) * 100);

  return (
    <div>
      {/* Barra de progresso */}
      <div style={{
        background: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: 20,
        marginBottom: 24,
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 12,
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.mute, letterSpacing: 1, textTransform: "uppercase" }}>
              Seu progresso
            </div>
            <div style={{ fontFamily: DISPLAY, fontSize: 28, fontWeight: 800, color: C.ink, marginTop: 2 }}>
              {done.size} <span style={{ color: C.mute, fontSize: 20 }}>/ {PASSOS.length}</span>
            </div>
          </div>
          <div style={{
            fontFamily: FONT, fontSize: 34, fontWeight: 800,
            color: C.teal,
          }}>
            {pct}%
          </div>
        </div>
        <div className="tut-progress-track">
          <div className="tut-progress-fill" style={{ width: `${pct}%` }}/>
        </div>
        {pct === 100 && (
          <div style={{
            marginTop: 16,
            padding: "14px 18px",
            background: C.tealSoft,
            border: "1px solid rgba(26,158,143,.3)",
            borderLeft: `3px solid ${C.teal}`,
            borderRadius: 10,
            display: "flex", alignItems: "center", gap: 12,
            animation: "tutBounceIn .6s both",
          }}>
            <span style={{ fontSize: 28 }}>🎉</span>
            <div>
              <div style={{ fontWeight: 800, color: C.ink, fontFamily: DISPLAY, fontSize: 18 }}>
                Missão cumprida!
              </div>
              <div style={{ fontSize: 13, color: C.sub }}>
                Você já domina o básico. Agora é só operar.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lista de passos */}
      <div className="tut-anim-stagger" style={{ position: "relative" }}>
        {PASSOS.map(p => {
          const isDone = done.has(p.id);
          return (
            <div key={p.id}
                 className={`tut-check-item ${isDone ? "done" : ""}`}
                 onClick={() => toggle(p.id)}>
              <div className="tut-check-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M 5 12 L 10 17 L 19 7" className="tut-check-svg"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div className="tut-check-label" style={{
                  fontWeight: 700, fontSize: 14.5, color: C.ink,
                }}>
                  {p.id}. {p.titulo}
                </div>
                <div style={{ fontSize: 13, color: C.sub, marginTop: 3 }}>
                  {p.descricao}
                </div>
                <div style={{
                  display: "inline-block", marginTop: 8,
                  fontSize: 11, fontWeight: 700, color: C.tealDeep,
                  background: C.tealSoft, padding: "3px 9px",
                  borderRadius: 6,
                }}>
                  📍 {p.onde}
                </div>
              </div>
            </div>
          );
        })}

        {/* Confetti burst */}
        {confetti > 0 && (
          <div key={confetti} style={{
            position: "absolute", top: -40, left: "50%",
            width: 0, height: 0, pointerEvents: "none",
          }}>
            {Array.from({ length: 24 }).map((_, i) => {
              const angle = (i / 24) * Math.PI * 2;
              const dist = 120 + Math.random() * 80;
              const tx = Math.cos(angle) * dist;
              const ty = Math.sin(angle) * dist - 60;
              const rot = (Math.random() - .5) * 720;
              const col = [C.teal, C.amber, C.violet, C.mint, C.sun][i % 5];
              return (
                <span key={i} className="tut-confetti-piece" style={{
                  "--tx": `${tx}px`,
                  "--ty": `${ty}px`,
                  "--r": `${rot}deg`,
                  background: col,
                  borderRadius: i % 2 ? "50%" : "2px",
                  animationDelay: `${Math.random() * .2}s`,
                }}/>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// STATUS CARDS (grid de 7)
// ═══════════════════════════════════════════════════════════════════════════
function StatusCardsGrid() {
  return (
    <div className="tut-anim-stagger" style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
      gap: 14,
    }}>
      {STATUSES.map(s => (
        <div key={s.key} className="tut-card" style={{
          borderLeft: `5px solid ${s.color}`,
          padding: 20,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span className="tut-pill" style={{ background: s.tint, color: s.color }}>
              {s.key}
            </span>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: s.tint, display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <CacambaSvg size={22} color={s.color} />
            </div>
          </div>
          <div style={{ fontSize: 13.5, color: C.ink, fontWeight: 600, lineHeight: 1.55, marginBottom: 10 }}>
            {s.resumo}
          </div>
          <div style={{
            paddingTop: 10, borderTop: `1px dashed ${C.line}`,
            fontSize: 12.5, color: C.sub, lineHeight: 1.5,
          }}>
            <strong style={{ color: s.color }}>→</strong> {s.oQueFazer}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FAQ ACCORDION
// ═══════════════════════════════════════════════════════════════════════════
const FAQ_ITEMS = [
  {
    q: "A caçamba travou em um status e não consigo avançar, o que faço?",
    a: <>
      Na aba <strong>Locações</strong>, abra o menu de ações daquela locação (os três pontinhos ao lado).
      Vai aparecer a próxima ação disponível (por exemplo, "Confirmar Retirada" se o status é Para Retirar).
      Se nenhuma ação aparecer, a locação provavelmente já está encerrada ou cancelada — verifique o histórico
      na aba <strong>Histórico</strong> dentro do detalhe da locação.
    </>
  },
  {
    q: "Como faço se o cliente sumiu e não pagou a caçamba?",
    a: <>
      Primeiro, tente contato via WhatsApp pelo sistema (botão direto na locação).
      Se não responder, mantenha a locação no status <strong>Para Cobrar</strong> e, quando decidir escrever como prejuízo,
      use a ação <strong>Cancelar</strong> com motivo "inadimplência". Fica registrado no histórico do cliente,
      que passa a aparecer como devedor nos relatórios.
    </>
  },
  {
    q: "Editei o valor de uma locação que já foi colocada. Isso é problema?",
    a: <>
      Não. Enquanto a locação não foi paga, você pode ajustar o valor livremente. A alteração fica registrada
      no <strong>Histórico da locação</strong> com quem fez e quando. Se a locação já foi marcada como paga,
      você precisa desmarcar o pagamento antes de alterar.
    </>
  },
  {
    q: "O motorista não está recebendo as notificações no app. Por quê?",
    a: <>
      Três coisas pra checar: <br/>
      1. <strong>Permissão de notificação</strong> está ativada no celular dele? Configurações do Android → Apps → Controla Motorista → Notificações.<br/>
      2. <strong>Bateria</strong>: alguns celulares (Xiaomi, Motorola) matam o app em segundo plano. Pede pra ele liberar "bateria sem restrição" pro Controla Motorista.<br/>
      3. <strong>Login ativo</strong>: se ele saiu e entrou de novo, o token de notificação pode ter mudado. Pede pra reabrir o app e aguardar alguns segundos.
    </>
  },
  {
    q: "Como exporto os dados pra contabilidade?",
    a: <>
      Menu <strong>Relatórios → Locações (Detalhado)</strong>. Escolha o período, as colunas que quer e exporte em Excel.
      Para nota fiscal, o CPF/CNPJ do cliente já está no arquivo. Para recibos em lote, use <strong>Relatório Mensal</strong>.
    </>
  },
  {
    q: "Preciso emitir nota fiscal. O Controla emite?",
    a: <>
      Nesta versão, o Controla <em>ainda não</em> emite NFSe automaticamente — você exporta os dados (ver FAQ acima) e emite no seu emissor
      municipal ou contabilidade. Integração direta com NFSe está no nosso planejamento. Se é prioridade pra você,
      abre um chamado que priorizamos.
    </>
  },
  {
    q: "Uma caçamba aparece em dois clientes ao mesmo tempo. Como resolvo?",
    a: <>
      Isso acontece quando a locação anterior não foi encerrada corretamente. Vá até o cliente antigo,
      localize a locação da caçamba, e confirme retirada/encerramento. O sistema evita conflitos ao criar
      nova locação, mas se já aconteceu, essa limpeza resolve.
    </>
  },
  {
    q: "Fiz uma troca mas as duas locações aparecem separadas. Estão vinculadas?",
    a: <>
      Sim — o sistema cria a locação filha automaticamente e marca o vínculo nas observações.
      Abra a locação nova, vá na aba <strong>Vínculos</strong> e você verá a locação pai (origem)
      e se ela também gerou outras filhas. A trilha de trocas fica preservada pra auditoria.
    </>
  },
  {
    q: "Como mudo a senha ou adiciono outro usuário?",
    a: <>
      Menu do seu avatar (canto superior direito) → <strong>Alterar Senha</strong>.
      Para adicionar usuários, <strong>Configurações → Usuários → Novo Usuário</strong> com permissões de Operacional ou Gestor.
    </>
  },
  {
    q: "Esqueci de marcar uma locação como paga. Como corrijo retroativamente?",
    a: <>
      Abra a locação, use a ação <strong>Confirmar Pagamento</strong> e escolha a data em que o pagamento realmente entrou.
      O sistema registra com data retroativa e atualiza os relatórios financeiros automaticamente.
    </>
  },
];

function FAQAccordion() {
  const [open, setOpen] = useState(0);
  return (
    <div>
      {FAQ_ITEMS.map((it, i) => (
        <div key={i} className={`tut-accordion-item ${open === i ? "open" : ""}`}>
          <button className="tut-accordion-head"
                  onClick={() => setOpen(open === i ? -1 : i)}>
            <span style={{
              flexShrink: 0,
              width: 28, height: 28, borderRadius: 8,
              background: open === i ? C.teal : C.tealSoft,
              color: open === i ? "#fff" : C.tealDeep,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 800,
              fontFamily: DISPLAY,
              transition: "all .2s",
            }}>
              {i + 1}
            </span>
            <span style={{ flex: 1 }}>{it.q}</span>
            <span className="tut-accordion-icon">+</span>
          </button>
          <div className="tut-accordion-body">
            <div className="tut-accordion-inner">{it.a}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// GLOSSÁRIO
// ═══════════════════════════════════════════════════════════════════════════
const GLOSSARIO = [
  { termo: "Locação", def: "Contratação de uma caçamba por um cliente em um endereço. É a entidade central do sistema — tudo gira em torno dela." },
  { termo: "Caçamba estacionária", def: "O equipamento físico (container de entulho) que fica parado no cliente recebendo resíduos." },
  { termo: "Status", def: "Etapa atual da locação no ciclo de vida. Existem 7: Para Colocar, Colocada, Para Retirar, Para Trocar, Para Cobrar, Encerrada e Cancelada." },
  { termo: "Troca", def: "Operação de substituir uma caçamba cheia por uma vazia no mesmo cliente. Gera duas locações vinculadas: a 'pai' (que encerra) e a 'filha' (nova)." },
  { termo: "Caçamba pai", def: "A locação anterior que foi substituída numa troca. Fica encerrada e vinculada à filha." },
  { termo: "Caçamba filha", def: "A nova locação criada quando acontece uma troca. Herda o cliente e endereço da pai." },
  { termo: "Itinerário", def: "Roteiro de atendimentos de um motorista em um dia. Agrupa várias locações (colocações, retiradas, trocas) em uma ordem otimizada." },
  { termo: "MTR / CTR", def: "Manifesto/Controle de Transporte de Resíduos. Documento obrigatório em obras que exige rastreabilidade do que foi descartado e onde." },
  { termo: "Classe de resíduo", def: "Classificação do lixo pela norma ABNT: A (inerte), B (recicláveis), C (sem tecnologia), D (perigoso)." },
  { termo: "Operação", def: "Tipo de serviço de uma locação: colocação, retirada ou troca." },
  { termo: "Aterro", def: "Local licenciado onde o motorista descarta o conteúdo da caçamba. Cadastrado no sistema com KM e custo." },
  { termo: "Tenant", def: "Cada empresa cliente do Controla. Sistema multi-empresa onde cada tenant vê apenas seus próprios dados." },
  { termo: "Plano", def: "Nível de assinatura do Controla. Diferencia quais funcionalidades ficam liberadas (Básico, Controla, Controla Plus)." },
];

function GlossarioSection() {
  return (
    <div className="tut-anim-stagger" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
      {GLOSSARIO.map(g => (
        <div key={g.termo} className="tut-card" style={{ padding: 18 }}>
          <div style={{
            fontFamily: DISPLAY,
            fontSize: 18, fontWeight: 800, color: C.ink,
            marginBottom: 6,
          }}>
            {g.termo}
          </div>
          <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.55 }}>
            {g.def}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// GIF PLACEHOLDER — espaço visual pra Thiago substituir por GIFs reais depois
// ═══════════════════════════════════════════════════════════════════════════
function GifPlaceholder({ titulo, descricao }) {
  return (
    <div className="tut-gif-ph" style={{ marginBottom: 24 }}>
      <div style={{ position: "relative", textAlign: "center", padding: 20 }}>
        <div style={{
          display: "inline-flex",
          width: 56, height: 56, borderRadius: "50%",
          background: "#fff",
          alignItems: "center", justifyContent: "center",
          marginBottom: 12,
          border: `2px solid ${C.teal}`,
          boxShadow: `0 4px 16px rgba(26,158,143,.2)`,
        }}>
          <span style={{ fontSize: 24 }}>🎬</span>
        </div>
        <div style={{
          fontFamily: DISPLAY,
          fontWeight: 700, fontSize: 18, color: C.ink,
          marginBottom: 6,
        }}>
          {titulo}
        </div>
        <div style={{ fontSize: 13, color: C.sub, maxWidth: 420, margin: "0 auto" }}>
          {descricao}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SEÇÃO DE PASSO-A-PASSO (steps numerados)
// ═══════════════════════════════════════════════════════════════════════════
function Steps({ items }) {
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.line}`,
      borderRadius: 14,
      padding: "20px 28px",
    }}>
      {items.map((s, i) => (
        <div key={i} className="tut-step">
          <div className="tut-step-num">{i + 1}</div>
          <div style={{ flex: 1, paddingTop: 3 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: C.ink, marginBottom: 4 }}>
              {s.titulo}
            </div>
            <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.6 }}>
              {s.texto}
            </div>
            {s.dica && (
              <div style={{
                marginTop: 8,
                padding: "9px 12px",
                background: C.amberSoft,
                border: "1px solid #fde68a",
                borderLeft: `3px solid ${C.amber}`,
                borderRadius: 8,
                fontSize: 13,
                lineHeight: 1.55,
                color: "#92400e",
              }}>
                💡 <strong>Dica:</strong> {s.dica}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ARTIGOS — conteúdo de cada página
// ═══════════════════════════════════════════════════════════════════════════

function BoasVindas({ onSelect }) {
  const cards = [
    {
      id: "primeiros-passos",
      emoji: "🚀",
      cor: C.teal,
      titulo: "Começar do zero",
      texto: "Um roteiro de 8 passos pra você sair hoje pronto pra operar.",
      tempo: "5 min"
    },
    {
      id: "ciclo-vida",
      emoji: "🔄",
      cor: C.amber,
      titulo: "Como funciona uma locação",
      texto: "Entenda o ciclo de vida completo, os 7 status e os caminhos possíveis.",
      tempo: "3 min"
    },
    {
      id: "troca",
      emoji: "♻️",
      cor: C.violet,
      titulo: "O que é uma Troca",
      texto: "Animação explicando como a gente mantém duas locações vinculadas.",
      tempo: "2 min"
    },
    {
      id: "faq",
      emoji: "❓",
      cor: C.mint,
      titulo: "Perguntas frequentes",
      texto: "As dúvidas mais comuns respondidas de forma direta.",
      tempo: "Consulta"
    },
  ];

  return (
    <div>
      <Hero onStart={onSelect}/>

      <div style={{ marginTop: 40 }}>
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: 1.5,
          color: C.teal, textTransform: "uppercase", marginBottom: 10,
        }}>
          Por onde começar
        </div>
        <h3 style={{
          fontFamily: FONT, fontSize: 18, fontWeight: 800,
          letterSpacing: "-.2px",
          color: C.ink, margin: "0 0 16px",
        }}>
          Escolha seu ponto de partida
        </h3>

        <div className="tut-anim-stagger" style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: 14,
        }}>
          {cards.map(c => (
            <button key={c.id} onClick={() => onSelect(c.id)}
                    className="tut-card"
                    style={{
                      textAlign: "left", cursor: "pointer",
                      background: C.surface, fontFamily: FONT,
                      borderTop: `4px solid ${c.cor}`,
                      display: "flex", flexDirection: "column",
                      alignItems: "stretch",
                    }}>
              <div style={{
                width: 38, height: 38, borderRadius: 10,
                background: C.bg, border: `1px solid ${C.line}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 18, marginBottom: 12,
              }}>{c.emoji}</div>
              <div style={{ fontFamily: FONT, fontSize: 15.5, fontWeight: 800, color: C.ink, marginBottom: 6 }}>
                {c.titulo}
              </div>
              <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.5, flex: 1, marginBottom: 12 }}>
                {c.texto}
              </div>
              <div style={{
                fontSize: 11, fontWeight: 700, color: c.cor,
                letterSpacing: .5, textTransform: "uppercase",
              }}>
                ⏱ {c.tempo} →
              </div>
            </button>
          ))}
        </div>
      </div>

      <div style={{
        marginTop: 32,
        padding: "20px 24px",
        background: C.tealSoft,
        borderRadius: 12,
        border: "1px solid rgba(26,158,143,.25)",
        borderLeft: `3px solid ${C.teal}`,
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <div style={{
          fontSize: 22, flexShrink: 0,
          width: 44, height: 44, borderRadius: 12,
          background: "#fff", border: "1px solid rgba(26,158,143,.25)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>💬</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, color: C.ink, fontSize: 15, fontFamily: FONT, marginBottom: 4 }}>
            Não achou o que procurava?
          </div>
          <div style={{ fontSize: 14, color: C.sub, lineHeight: 1.6 }}>
            Entre em contato com o suporte direto pelo WhatsApp. A gente adiciona o tutorial que falta aqui assim que resolver a sua dúvida.
          </div>
        </div>
      </div>
    </div>
  );
}

function ArtigoPrimeirosPassos() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Roteiro de 8 passos"
        emoji="🚀"
        title="Primeiros Passos"
        subtitle="Marque cada item conforme for fazendo. Em 15-20 minutos você sai do zero pronto pra operar."
      />
      <PrimeirosPassosChecklist/>
    </div>
  );
}

function ArtigoCicloVida() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="O conceito mais importante"
        emoji="🔄"
        title="O Ciclo de Vida de uma Locação"
        subtitle="Toda locação começa em Para Colocar e termina em Encerrada (ou Cancelada). No meio, passa por status que dizem exatamente o que fazer em cada momento."
      />

      <CicloVidaDiagram/>

      <div style={{ marginTop: 48 }}>
        <h3 style={{ fontFamily: FONT, fontSize: 18, color: C.ink, margin: "0 0 10px", fontWeight: 800 }}>
          O caminho normal
        </h3>
        <p style={{ fontSize: 15, color: C.sub, lineHeight: 1.65, margin: "0 0 24px", maxWidth: 720 }}>
          Em 80% dos casos, uma locação vai assim: <strong style={{ color: C.tealDeep }}>Para Colocar → Colocada → Para Retirar → Para Cobrar → Encerrada</strong>.
          Cinco etapas. Cada uma com uma ação clara de quem é a bola.
        </p>

        <h3 style={{ fontFamily: FONT, fontSize: 18, color: C.ink, margin: "32px 0 10px", fontWeight: 800 }}>
          Os desvios
        </h3>
        <p style={{ fontSize: 15, color: C.sub, lineHeight: 1.65, margin: "0 0 14px", maxWidth: 720 }}>
          Existem dois desvios possíveis:
        </p>
        <div className="tut-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
          <div className="tut-card" style={{ borderLeft: `4px solid ${C.amber}` }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.ink, marginBottom: 6, fontFamily: DISPLAY }}>
              ♻️ Troca
            </div>
            <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.6 }}>
              Cliente liga pedindo pra trocar a caçamba cheia por uma vazia. A locação vai pra <em>Para Trocar</em>,
              motorista faz a troca, ao confirmar: locação original encerra e nasce uma nova filha no mesmo cliente.
            </div>
          </div>
          <div className="tut-card" style={{ borderLeft: `4px solid ${C.coral}` }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.ink, marginBottom: 6, fontFamily: DISPLAY }}>
              🚫 Cancelamento
            </div>
            <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.6 }}>
              Pode acontecer em qualquer ponto antes de encerrar: cliente desistiu, endereço errado, inadimplência.
              Usa a ação <em>Cancelar</em> e registra o motivo. Fica no histórico, não vira cobrança.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ArtigoTroca() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Conceito-chave"
        emoji="♻️"
        title="Como funciona uma Troca"
        subtitle="Uma troca não é uma simples atualização. O sistema cria automaticamente duas locações vinculadas — pai e filha — pra preservar o histórico contábil e operacional."
      />

      <TrocaAnimacao/>

      <div style={{ marginTop: 40 }}>
        <h3 style={{ fontFamily: FONT, fontSize: 18, color: C.ink, margin: "0 0 14px", fontWeight: 800 }}>
          Por que duas locações?
        </h3>
        <p style={{ fontSize: 15, color: C.sub, lineHeight: 1.65, margin: "0 0 14px", maxWidth: 720 }}>
          Se a gente apenas trocasse a caçamba da locação original, você perderia dado importante:
          quanto tempo a primeira ficou no cliente, qual motorista colocou, qual motorista trocou, e qual o valor cobrado da primeira vs. da segunda.
        </p>
        <p style={{ fontSize: 15, color: C.sub, lineHeight: 1.65, margin: "0 0 24px", maxWidth: 720 }}>
          Com o vínculo <strong>pai → filha</strong>, todo dado da primeira fica preservado,
          a segunda começa fresca com seu próprio relógio, e você enxerga a cadeia inteira na aba Vínculos de qualquer das duas.
        </p>
      </div>

      <div style={{ marginTop: 16 }}>
        <h3 style={{ fontFamily: FONT, fontSize: 18, color: C.ink, margin: "0 0 14px", fontWeight: 800 }}>
          Na prática, como se faz
        </h3>
        <Steps items={[
          { titulo: "Cliente solicita a troca", texto: "Você recebe ligação ou WhatsApp. Abra a locação atual da caçamba no cliente." },
          { titulo: "Use a ação 'Solicitar Troca'", texto: "No menu da locação, clique em 'Solicitar Troca'. O status muda pra Para Trocar e vai pro itinerário do motorista.", dica: "Se o motorista já estiver próximo, programe pra hoje mesmo." },
          { titulo: "Motorista executa a troca", texto: "Chega no cliente, retira a caçamba cheia, deixa a vazia nova. No app, ele registra." },
          { titulo: "Você confirma a troca no sistema", texto: "Usa a ação 'Confirmar Troca', escolhe a nova caçamba, define o valor da nova locação." },
          { titulo: "Dois registros aparecem", texto: "Locação #anterior vira Encerrada (troca). Nasce a #nova em Colocada. Ambas se enxergam na aba Vínculos." },
        ]}/>
      </div>
    </div>
  );
}

function ArtigoStatus() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Referência visual"
        emoji="🎨"
        title="Os 7 Status explicados"
        subtitle="Cada caçamba do seu estoque sempre está em um desses 7 estados. A cor te ajuda a bater o olho e saber o que fazer."
      />
      <StatusCardsGrid/>
    </div>
  );
}

function ArtigoCadastros() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Organização dos dados"
        emoji="📇"
        title="Cliente, Contato e Endereço"
        subtitle="Esses três conceitos se confundem muito. Aqui você entende de vez a diferença."
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14, marginBottom: 32 }}
           className="tut-anim-stagger">
        <div className="tut-card" style={{ borderTop: `4px solid ${C.teal}` }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: C.tealSoft, border: "1px solid rgba(26,158,143,.22)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, marginBottom: 10 }}>🏢</div>
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 18, color: C.ink, marginBottom: 6 }}>
            Cliente
          </div>
          <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.55 }}>
            <strong>Quem paga.</strong> Pessoa física ou jurídica. Tem CPF ou CNPJ, razão social, condições de pagamento.
            Um cliente tem <em>um</em> CPF/CNPJ e aparece no recibo.
          </div>
        </div>
        <div className="tut-card" style={{ borderTop: `4px solid ${C.amber}` }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: C.amberSoft, border: "1px solid rgba(245,158,11,.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, marginBottom: 10 }}>👤</div>
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 18, color: C.ink, marginBottom: 6 }}>
            Contato
          </div>
          <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.55 }}>
            <strong>Quem fala.</strong> A pessoa que atende o telefone na obra. Um cliente pode ter vários contatos:
            o engenheiro, o mestre de obra, a secretária. Cada um com seu telefone.
          </div>
        </div>
        <div className="tut-card" style={{ borderTop: `4px solid ${C.violet}` }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: C.violetSoft, border: "1px solid rgba(139,92,246,.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, marginBottom: 10 }}>📍</div>
          <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 18, color: C.ink, marginBottom: 6 }}>
            Endereço
          </div>
          <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.55 }}>
            <strong>Onde a caçamba vai.</strong> Um cliente pode ter vários endereços cadastrados (obra 1, obra 2, escritório).
            Cada locação usa um endereço específico.
          </div>
        </div>
      </div>

      <div style={{
        background: C.tealSoft,
        border: "1px solid rgba(26,158,143,.25)",
        borderLeft: `3px solid ${C.teal}`,
        borderRadius: 12,
        padding: "20px 24px",
      }}>
        <div style={{ fontFamily: FONT, fontWeight: 800, fontSize: 15, color: C.ink, marginBottom: 8 }}>
          Exemplo prático
        </div>
        <p style={{ fontSize: 14, color: C.sub, lineHeight: 1.7, margin: 0 }}>
          <strong style={{ color: C.ink }}>Construtora Aurora Ltda.</strong> é o <strong>cliente</strong> (CNPJ: 12.345.678/0001-90, com condição de pagamento "30 dias").
          Ela tem dois <strong>contatos</strong>: João (engenheiro, faz os pedidos) e Clara (financeiro, paga as faturas).
          E tem três <strong>endereços</strong> cadastrados: Obra Vila Nova, Obra Centro e o Escritório.
          Você cria uma locação pra ela escolhendo qual endereço é (Obra Vila Nova) e qual contato atende ali (João).
        </p>
      </div>
    </div>
  );
}

function ArtigoCriarLocacao() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Como fazer"
        emoji="➕"
        title="Criar uma nova Locação"
        subtitle="O fluxo central do Controla. Cada caçamba que sai do seu pátio começa aqui."
      />

      <GifPlaceholder
        titulo="📹 Grave aqui: como cadastrar uma locação em 30 segundos"
        descricao="Sugestão: abrir Locações → Nova Locação → escolher cliente → escolher endereço → tipo de caçamba → motorista → salvar."
      />

      <Steps items={[
        { titulo: "Abra a aba Locações", texto: "No menu lateral esquerdo, clique em Locações. Você vê a lista filtrada por status." },
        { titulo: "Clique em + Nova Locação", texto: "Botão no topo direito, em destaque teal." },
        { titulo: "Selecione o cliente", texto: "Digite parte do nome ou CPF/CNPJ. Se o cliente ainda não existe, use o botão 'Criar cliente inline' sem sair da tela.", dica: "Cliente novo precisa de pelo menos nome e telefone. CPF/CNPJ pode completar depois." },
        { titulo: "Escolha o endereço e o contato", texto: "Se o cliente tem mais de um endereço cadastrado, selecione qual. Idem pro contato da obra." },
        { titulo: "Defina o tipo de caçamba", texto: "Puxa o valor padrão do tipo (ex: 5 m³ = R$ 250). Você pode editar o valor pra essa locação específica." },
        { titulo: "Atribua motorista e data prevista", texto: "Escolhe quem vai colocar e quando. O motorista recebe automaticamente no app dele." },
        { titulo: "Salva", texto: "Locação nasce com status Para Colocar. Já aparece no itinerário do motorista." },
      ]}/>
    </div>
  );
}

function ArtigoConfirmarColocacao() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Como fazer"
        emoji="✅"
        title="Confirmar a colocação"
        subtitle="Quando o motorista efetivamente coloca a caçamba, você confirma no sistema. O status vira Colocada e começa a contar o tempo no cliente."
      />

      <GifPlaceholder
        titulo="📹 Grave aqui: confirmação de colocação via painel web"
        descricao="Sugestão: na aba Locações com filtro Para Colocar, clicar nos três pontinhos de uma locação → Confirmar Colocação → registrar data e caçamba."
      />

      <Steps items={[
        { titulo: "Filtre pelo status Para Colocar", texto: "No topo da lista, clique na pílula verde-teal 'Para Colocar'. Você vê só o que ainda não foi colocado." },
        { titulo: "Abra o menu da locação", texto: "Os três pontinhos ao lado de cada linha." },
        { titulo: "Clique em 'Confirmar Colocação'", texto: "Abre um modal." },
        { titulo: "Escolha a caçamba física", texto: "Selecione do estoque qual caçamba foi deixada. Se tem foto da caçamba no local, anexe aqui.", dica: "Anexar foto é opcional mas vira prova visual na aba Documentos — evita discussão com o cliente." },
        { titulo: "Confirme a data real", texto: "Por padrão é hoje, mas você pode ajustar se foi ontem e esqueceu de marcar." },
        { titulo: "Pronto", texto: "Status vai pra Colocada. A caçamba some do estoque disponível. O relógio de 'dias no cliente' começa a rodar." },
      ]}/>
    </div>
  );
}

function ArtigoPedirRetirada() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Como fazer"
        emoji="📞"
        title="Solicitar retirada"
        subtitle="Cliente ligou pedindo pra buscar. Em 4 cliques você programa a retirada."
      />

      <GifPlaceholder
        titulo="📹 Grave aqui: fluxo de solicitar retirada"
        descricao="Sugestão: Locações → filtro Colocada → menu da locação → Solicitar Retirada → escolher motorista → confirmar."
      />

      <Steps items={[
        { titulo: "Localize a locação", texto: "Filtro Colocada, busca pelo cliente ou código da caçamba." },
        { titulo: "Abra o menu → Solicitar Retirada", texto: "Status vira Para Retirar." },
        { titulo: "Escolha o motorista da retirada", texto: "Pode ser o mesmo que colocou ou outro. Ele recebe no app automaticamente." },
        { titulo: "Programe a data prevista", texto: "Hoje, amanhã, ou uma data específica. O motorista vê no itinerário dele." },
        { titulo: "Confirme a retirada quando ele fizer", texto: "Quando o motorista terminar, use a ação 'Confirmar Retirada'. Status vai pra Para Cobrar.", dica: "Se já está pago, pule direto de Para Retirar pra Encerrada usando Confirmar Pagamento." },
      ]}/>
    </div>
  );
}

function ArtigoFazerTroca() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Como fazer"
        emoji="🔁"
        title="Fazer uma troca"
        subtitle="Cliente pediu pra trocar a caçamba cheia por vazia. Veja o fluxo completo, passo por passo."
      />

      <GifPlaceholder
        titulo="📹 Grave aqui: troca completa"
        descricao="Sugestão: Locação colocada → Solicitar Troca → motorista vai → Confirmar Troca → duas locações vinculadas no sistema."
      />

      <Steps items={[
        { titulo: "Cliente solicita a troca", texto: "Normalmente por telefone ou WhatsApp." },
        { titulo: "Abra a locação atual e use 'Solicitar Troca'", texto: "Menu da locação → Solicitar Troca. Status vira Para Trocar." },
        { titulo: "Atribua motorista e data", texto: "Idem fluxo de retirada. Aparece no itinerário dele." },
        { titulo: "Motorista executa a troca", texto: "Retira a cheia, deixa a vazia." },
        { titulo: "Use 'Confirmar Troca' no sistema", texto: "Aqui o sistema pede a nova caçamba e o valor da nova locação. Você confirma." },
        { titulo: "Duas locações ficam vinculadas", texto: "A original (pai) vira Encerrada. A nova (filha) nasce em Colocada. Ambas se enxergam na aba Vínculos.", dica: "Veja o artigo Como funciona uma Troca pra entender o motivo de serem duas locações." },
      ]}/>
    </div>
  );
}

function ArtigoGerarRecibo() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Como fazer"
        emoji="🧾"
        title="Gerar recibo"
        subtitle="Recibo impresso, rápido, com dados da sua empresa e da locação. Entrega pro cliente na hora."
      />

      <GifPlaceholder
        titulo="📹 Grave aqui: geração de recibo"
        descricao="Sugestão: abrir uma locação → botão Gerar Recibo → preview imprimível."
      />

      <Steps items={[
        { titulo: "Abra o menu da locação", texto: "Três pontinhos ao lado da locação na lista." },
        { titulo: "Clique em 'Gerar Recibo'", texto: "Abre uma nova janela com o recibo formatado, com logo da sua empresa no topo." },
        { titulo: "Imprima ou salve em PDF", texto: "A janela dispara o diálogo de impressão do navegador. Escolha impressora ou 'Salvar como PDF'." },
        { titulo: "Entregue pro cliente", texto: "Pode imprimir e entregar física, ou mandar o PDF por WhatsApp direto do sistema.", dica: "Se quiser personalizar cabeçalho/logo, vá em Configurações → Empresa e suba sua logo em PNG." },
      ]}/>
    </div>
  );
}

function ArtigoFAQ() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Referência"
        emoji="❓"
        title="Perguntas frequentes"
        subtitle="Dúvidas reais de quem usa o sistema todo dia. Clique pra expandir a resposta."
      />
      <FAQAccordion/>
    </div>
  );
}

function ArtigoGlossario() {
  return (
    <div className="tut-anim-in">
      <ArticleHeader
        eyebrow="Dicionário do sistema"
        emoji="📚"
        title="Glossário"
        subtitle="Termos usados no Controla e no mundo de locação de caçambas. Consulta rápida."
      />
      <GlossarioSection/>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN EXPORT — monta a aba completa
// ═══════════════════════════════════════════════════════════════════════════
export default function TutorialPage() {
  const [active, setActive] = useState("boas-vindas");
  const contentRef = useRef(null);

  // Scrolla pro topo do conteúdo ao trocar de artigo
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
    // Scrolla também a página inteira pro topo
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch {}
  }, [active]);

  const renderArticle = () => {
    switch (active) {
      case "boas-vindas":       return <BoasVindas onSelect={setActive}/>;
      case "primeiros-passos":  return <ArtigoPrimeirosPassos/>;
      case "ciclo-vida":        return <ArtigoCicloVida/>;
      case "troca":             return <ArtigoTroca/>;
      case "status":            return <ArtigoStatus/>;
      case "cadastros":         return <ArtigoCadastros/>;
      case "criar-locacao":     return <ArtigoCriarLocacao/>;
      case "confirmar-coloc":   return <ArtigoConfirmarColocacao/>;
      case "pedir-retirada":    return <ArtigoPedirRetirada/>;
      case "fazer-troca":       return <ArtigoFazerTroca/>;
      case "gerar-recibo":      return <ArtigoGerarRecibo/>;
      case "faq":               return <ArtigoFAQ/>;
      case "glossario":         return <ArtigoGlossario/>;
      default:                  return <BoasVindas onSelect={setActive}/>;
    }
  };

  return (
    <div className="tut-root" style={{
      background: C.bg,
      fontFamily: FONT,
      color: C.ink,
      minHeight: "100%",
      padding: "24px 28px",
    }}>
      <TutorialStyles/>

      <div className="tut-layout" style={{
        display: "grid",
        gridTemplateColumns: "250px 1fr",
        gap: 28,
        maxWidth: 1240,
        margin: "0 auto",
      }}>
        <Sidebar active={active} onSelect={setActive}/>
        <main ref={contentRef} style={{ minWidth: 0 }}>
          <div key={active}>
            {renderArticle()}
          </div>
        </main>
      </div>
    </div>
  );
}
